// «Норм чи Стрьом» result views: a two-slice pie for one statement and the
// end-of-game recap list. Pure CSS (conic-gradient) — no chart library.
import { useT, TFunc } from '../i18n';
import type { VoteLabels, VoteSummaryEntry, VoteTally } from '../shared/protocol';

/** Button wording for a game: the template's custom labels, else the defaults. */
export function useVoteLabels(custom?: VoteLabels): VoteLabels {
  const t = useT();
  return voteLabels(t, custom);
}

export function voteLabels(t: TFunc, custom?: VoteLabels): VoteLabels {
  if (custom?.norm && custom?.cringe) return custom;
  return { norm: t('play.voteNorm'), cringe: t('play.voteCringe') };
}

/** Whole-number percentages that always sum to 100 (cringe takes the remainder). */
export function splitPercent(t: VoteTally): { norm: number; cringe: number } {
  if (t.total <= 0) return { norm: 0, cringe: 0 };
  const norm = Math.round((t.norm / t.total) * 100);
  return { norm, cringe: 100 - norm };
}

export function VotePie({ tally, labels, compact = false }: { tally: VoteTally; labels: VoteLabels; compact?: boolean }) {
  const t = useT();
  const pct = splitPercent(tally);
  if (tally.total === 0) {
    return <div className="muted centered pie-empty">{t('play.nobodyVoted')}</div>;
  }
  // The green/red pair is close under deutan vision, so both slices carry a
  // direct label (name + %) and a 2px surface gap separates them.
  const gap = pct.norm > 0 && pct.cringe > 0 ? 'var(--panel)' : 'transparent';
  const style = {
    background: `conic-gradient(var(--norm-c) 0 ${pct.norm}%, ${gap} ${pct.norm}% calc(${pct.norm}% + 2px), var(--cringe-c) calc(${pct.norm}% + 2px) 100%)`,
  };
  return (
    <div className={`pie-wrap ${compact ? 'compact' : ''}`}>
      <div className="pie" style={style} role="img" aria-label={`${labels.norm} ${pct.norm}%, ${labels.cringe} ${pct.cringe}%`} />
      <ul className="pie-legend">
        <li className={tally.correct === 'norm' ? 'correct' : ''}>
          <span className="swatch norm" aria-hidden="true" />
          <span className="lbl">{labels.norm}{tally.correct === 'norm' && ' ✓'}</span>
          <span className="val">{pct.norm}% <em>({tally.norm})</em></span>
        </li>
        <li className={tally.correct === 'cringe' ? 'correct' : ''}>
          <span className="swatch cringe" aria-hidden="true" />
          <span className="lbl">{labels.cringe}{tally.correct === 'cringe' && ' ✓'}</span>
          <span className="val">{pct.cringe}% <em>({tally.cringe})</em></span>
        </li>
        {tally.correct && (
          <li className="answerline">
            {t('play.correctAnswer', { answer: tally.correct === 'norm' ? labels.norm : labels.cringe })}
          </li>
        )}
      </ul>
    </div>
  );
}

/** End-of-game recap: every statement with its split, in play order. */
export function VoteSummary({ entries, labels }: { entries: VoteSummaryEntry[]; labels: VoteLabels }) {
  const t = useT();
  if (entries.length === 0) return <p className="muted">{t('play.nobodyVoted')}</p>;
  let lastTopic = '';
  return (
    <>
      <div className="vs-legend">
        <span><span className="swatch norm" aria-hidden="true" /> {labels.norm}</span>
        <span><span className="swatch cringe" aria-hidden="true" /> {labels.cringe}</span>
      </div>
      <ol className="votesummary">
        {entries.map((e) => {
          const pct = splitPercent(e.tally);
          const showTopic = e.topicTitle !== lastTopic;
          lastTopic = e.topicTitle;
          return (
            <li key={e.questionId}>
              {showTopic && <div className="vs-topic">{e.topicTitle}</div>}
              <div className="vs-text">
                {e.text}
                {e.tally.correct && (
                  <span className="vs-correct"> ✓ {e.tally.correct === 'norm' ? labels.norm : labels.cringe}</span>
                )}
              </div>
              {e.tally.total === 0 ? (
                <div className="muted small">{t('play.nobodyVoted')}</div>
              ) : (
                <div className="vs-bar" role="img" aria-label={`${labels.norm} ${pct.norm}%, ${labels.cringe} ${pct.cringe}%`}>
                  <span className="norm" style={{ width: `${pct.norm}%` }}>{pct.norm > 12 ? `${pct.norm}%` : ''}</span>
                  <span className="cringe" style={{ width: `${pct.cringe}%` }}>{pct.cringe > 12 ? `${pct.cringe}%` : ''}</span>
                </div>
              )}
            </li>
          );
        })}
      </ol>
    </>
  );
}
